import { Skeleton } from "@/components/Skeleton";
import { cn } from "@/utils/cn";

interface CardSkeletonProps {
  /** Width of the card */
  width?: string | number;
  /** Height of the card */
  height?: string | number;
  /** Show header section */
  withHeader?: boolean;
  /** Show avatar in header */
  withAvatar?: boolean;
  /** Number of content lines */
  contentLines?: number;
  /** Show footer section */
  withFooter?: boolean;
  /** Custom className */
  className?: string;
  /** Padding inside the card */
  padding?: string;
}

/**
 * CardSkeleton Component
 * Skeleton for card layouts with customizable sections
 */
export const CardSkeleton = ({
  width = "100%",
  height,
  withHeader = false,
  withAvatar = false,
  contentLines = 3,
  withFooter = false,
  className,
  padding = "1rem",
}: CardSkeletonProps) => {
  // If height is provided, show a simple rectangular skeleton
  if (height) {
    return (
      <Skeleton
        width={width}
        height={height}
        variant="rectangular"
        radius="0.75rem"
        className={className}
      />
    );
  }

  // Otherwise, show a detailed card skeleton with sections
  return (
    <div
      className={cn("bg-theme-bg-surface rounded-xl border border-theme-border-subtle", className)}
      style={{ width: typeof width === "number" ? `${width}px` : width, padding }}
    >
      {/* Header Section */}
      {withHeader && (
        <div className="flex items-center gap-3 mb-4">
          {withAvatar && <Skeleton width={48} height={48} variant="circular" />}
          <div className="flex-1">
            <Skeleton width="60%" height="1.25rem" className="mb-2" />
            <Skeleton width="40%" height="0.875rem" />
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="space-y-3">
        {Array.from({ length: contentLines }).map((_, index) => {
          const isLastLine = index === contentLines - 1;
          return (
            <Skeleton
              key={index}
              width={isLastLine ? "70%" : "100%"}
              height="0.875rem"
              variant="text"
            />
          );
        })}
      </div>

      {/* Footer Section */}
      {withFooter && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-theme-border-subtle">
          <Skeleton width="80px" height="2rem" radius="0.375rem" />
          <Skeleton width="80px" height="2rem" radius="0.375rem" />
        </div>
      )}
    </div>
  );
};

CardSkeleton.displayName = "CardSkeleton";
